/** Umumiy UI qismlari. */

import { api } from "./api";
import { useRef, useState, type ReactNode } from "react";
import {
  flagLabel,
  fundingLabel,
  levelLabel,
  activeFlags,
  countryName,
  daysLeftLabel,
  flagEmoji,
  urgencyOf,
} from "./format";
import {
  IconAlert,
  IconBookmarkFilled,
  IconCompass,
  IconExternal,
  IconMapPin,
  MapPreview,
  TypeIcon,
} from "./icons";
import { t } from "./i18n";
import { navigate } from "./router";
import type { OpportunityCard } from "./types";

export function Spinner({ label }: { label?: string }) {
  return (
    <div className="center muted" role="status" aria-live="polite">
      <div className="spinner" />
      {label ? <p>{label}</p> : null}
    </div>
  );
}

export function ErrorView({
  message,
  onRetry,
}: {
  message: string;
  onRetry?: () => void;
}) {
  return (
    <div className="center">
      <div className="state-icon state-icon-danger">
        <IconAlert size={26} />
      </div>
      <p className="error">{message}</p>
      {onRetry ? (
        <button className="btn btn-retry" onClick={onRetry}>
          {t("common.retry")}
        </button>
      ) : null}
    </div>
  );
}

export function EmptyState({
  icon,
  title,
  hint,
  action,
}: {
  /** Bo'sh holat ikonkasi. Berilmasa — kompas (hech narsa topilmadi). */
  icon?: ReactNode;
  title: string;
  hint?: string;
  action?: ReactNode;
}) {
  return (
    <div className="center">
      <div className="state-icon">{icon ?? <IconCompass size={26} />}</div>
      <p className="empty-title">{title}</p>
      {hint ? <p className="muted">{hint}</p> : null}
      {action}
    </div>
  );
}

export function DeadlineBadge({
  days,
  compact = false,
}: {
  days: number | null;
  compact?: boolean;
}) {
  const label = daysLeftLabel(days);
  if (!label) {
    return compact ? null : (
      <span className="badge badge-unknown">{t("card.deadlineUnknown")}</span>
    );
  }
  return <span className={`badge badge-${urgencyOf(days)}`}>{label}</span>;
}

export function Chip({
  children,
  active = false,
  onClick,
  count,
}: {
  children: ReactNode;
  active?: boolean;
  onClick?: () => void;
  count?: number;
}) {
  const Tag = onClick ? "button" : "span";
  return (
    <Tag
      className={`chip${active ? " chip-active" : ""}`}
      onClick={onClick}
      type={onClick ? "button" : undefined}
      aria-pressed={onClick ? active : undefined}
    >
      {children}
      {count !== undefined ? <b className="chip-count">{count}</b> : null}
    </Tag>
  );
}

export function CardSkeleton() {
  return (
    <div className="card skeleton-card">
      <div className="skeleton-head">
        <div className="skeleton-box skeleton-icon" />
        <div className="skeleton-box skeleton-title" />
      </div>
      <div className="skeleton-box skeleton-meta" />
      <div className="skeleton-pills">
        <div className="skeleton-box skeleton-pill" />
        <div className="skeleton-box skeleton-pill" />
      </div>
      <div className="skeleton-box skeleton-badge" />
    </div>
  );
}

export function CardListSkeleton({ count = 4 }: { count?: number }) {
  return (
    <div className="card-list" aria-busy="true" aria-label="Yuklanmoqda">
      {Array.from({ length: count }).map((_, i) => (
        <CardSkeleton key={i} />
      ))}
    </div>
  );
}

export function Card({
  item,
  source,
}: {
  item: OpportunityCard;
  source?: string;
}) {
  const flags = activeFlags(item.flags);
  const facts = [
    item.funding ? fundingLabel(item.funding) : null,
    item.degree_level[0]
      ? levelLabel(item.degree_level[0])
      : null,
  ].filter(Boolean) as string[];

  const timerRef = useRef<number | null>(null);

  const startPrefetch = (delay = 120) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      api.prefetchDetail(item.id, source);
    }, delay);
  };

  const cancelPrefetch = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  return (
    <button
      className="card"
      type="button"
      onTouchStart={() => startPrefetch()}
      onTouchMove={cancelPrefetch}
      onTouchEnd={cancelPrefetch}
      // Sichqoncha kartada TO'XTAGANDA — ustidan o'tib ketganda emas.
      // Ilgari har karta darhol yuklanardi: ro'yxat ustida g'ildirak
      // aylantirilsa o'nlab so'rov ketib, server 429 qaytarardi.
      onMouseEnter={() => startPrefetch(250)}
      onMouseLeave={cancelPrefetch}
      onClick={() =>
        navigate(
          source
            ? { name: "detail", id: item.id, source }
            : { name: "detail", id: item.id },
        )
      }
    >
      <div className="card-head">
        <span className="card-icon">
          <TypeIcon type={item.type} size={19} />
        </span>
        <h3 className="card-title">{item.title}</h3>
        {item.saved ? (
          <span className="card-saved" title="Saqlangan">
            <IconBookmarkFilled size={17} />
          </span>
        ) : null}
      </div>

      <div className="card-meta">
        {item.organization ? <span>{item.organization}</span> : null}
        {item.host_country ? (
          <span>
            {flagEmoji(item.host_country)} {item.host_country}
          </span>
        ) : null}
      </div>

      <div className="card-facts">
        {facts.map((fact) => (
          <span key={fact} className="pill">
            {fact}
          </span>
        ))}
        {flags.slice(0, 2).map((flag) => (
          <span key={flag} className="pill pill-good">
            {flagLabel(flag)}
          </span>
        ))}
      </div>

      <DeadlineBadge days={item.days_left} />
    </button>
  );
}

export function CardList({
  items,
  source,
}: {
  items: OpportunityCard[];
  source?: string;
}) {
  return (
    <div className="card-list">
      {items.map((item) => (
        <Card key={item.id} item={item} source={source} />
      ))}
    </div>
  );
}

/**
 * Joylashuv xaritasi.
 *
 * NEGA BOSILGANDA YUKLANADI
 * -------------------------
 * Xarita tashqi xizmatdan keladi. Uni sahifa ochilishi bilan yuklasak:
 *   1. Har bir grant sahifasi ~1 MB ortiq trafik oladi — Mini App'ning
 *      asosiy ustunligi tez ochilishi (TZ §1.3), buni yo'qotmaymiz.
 *   2. Foydalanuvchining IP'si va qaysi grantni ko'rayotgani so'ramasdan
 *      uchinchi tomonga ketadi.
 * Shuning uchun avval joy nomi ko'rsatiladi, xarita esa foydalanuvchi
 * bosganda yuklanadi — bu ongli tanlov bo'ladi.
 *
 * DIQQAT: `output=embed` Google'ning rasmiy hujjatlashtirilgan API'si
 * emas. U ishlamay qolsa yoki Telegram ichida bloklansa, pastdagi
 * "Xaritada ochish" havolasi baribir ishlaydi — shuning uchun u har doim
 * ko'rsatiladi.
 */
export function MapCard({
  organization,
  country,
}: {
  organization: string | null;
  country: string | null;
}) {
  const [shown, setShown] = useState(false);

  const parts = [organization, countryName(country)].filter(Boolean);
  if (!parts.length) return null;
  const query = parts.join(", ");
  const embed = `https://maps.google.com/maps?q=${encodeURIComponent(query)}&output=embed`;
  const external = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;

  return (
    <div className="map-card">
      {shown ? (
        <iframe
          className="map-frame"
          src={embed}
          title={`Xarita: ${query}`}
          loading="lazy"
          // Qaysi sahifadan kelganimiz tashqi xizmatga yuborilmasin
          referrerPolicy="no-referrer"
          sandbox="allow-scripts allow-same-origin allow-popups"
        />
      ) : (
        <button
          className="map-placeholder"
          type="button"
          onClick={() => setShown(true)}
          aria-label={`Xaritani yuklash: ${query}`}
        >
          {/* Chizma o'rinbosar — tashqi so'rov YO'Q */}
          <MapPreview className="map-preview" />
          <span className="map-pin">
            <IconMapPin size={20} />
          </span>
          <span className="map-label">{query}</span>
        </button>
      )}
      <a
        className="map-open"
        href={external}
        target="_blank"
        rel="noopener noreferrer"
      >
        {t("card.openMap")}
        <IconExternal size={14} />
      </a>
    </div>
  );
}

export function Section({
  title,
  icon,
  action,
  children,
}: {
  title: string;
  /** Sarlavha yonidagi ikonka (oltin shisha kvadratda). */
  icon?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="section">
      <div className="section-head">
        <div className="section-title">
          {icon ? <span className="section-icon">{icon}</span> : null}
          <h2>{title}</h2>
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}
